export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details: unknown
  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message)
    this.code = code
    this.status = status
    this.details = details
  }
}

type ErrorBody = { error?: { code?: string; message?: string; details?: unknown } }

async function handle<T>(res: Response): Promise<T> {
  if (res.ok) return (await res.json()) as T
  let body: ErrorBody = {}
  try {
    body = (await res.json()) as ErrorBody
  } catch {
    body = {}
  }
  throw new ApiError(body.error?.code ?? String(res.status), body.error?.message ?? res.statusText, res.status, body.error?.details)
}

export const api = {
  get: <T>(path: string) => fetch(`/api${path}`, { credentials: 'same-origin' }).then(r => handle<T>(r)),
  post: <T>(path: string, body?: unknown) =>
    fetch(`/api${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    }).then(r => handle<T>(r)),
  patch: <T>(path: string, body: unknown) =>
    fetch(`/api${path}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).then(r => handle<T>(r)),
  put: <T>(path: string, body: unknown) =>
    fetch(`/api${path}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).then(r => handle<T>(r)),
  del: <T>(path: string) => fetch(`/api${path}`, { method: 'DELETE', credentials: 'same-origin' }).then(r => handle<T>(r)),
  upload: <T>(path: string, file: Blob, name: string) => {
    const fd = new FormData()
    fd.append('file', file, name)
    return fetch(`/api${path}`, { method: 'POST', credentials: 'same-origin', body: fd }).then(r => handle<T>(r))
  },
}

/** POST that answers with server-sent events; resolves when the stream ends. */
export async function streamPost<E>(path: string, body: unknown, onEvent: (e: E) => void, signal?: AbortSignal): Promise<void> {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body ?? {}),
    signal,
  })
  if (!res.ok || !res.body) {
    await handle(res)
    return
  }
  const reader = res.body.getReader()
  const dec = new TextDecoder()
  let buf = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buf += dec.decode(value, { stream: true })
    let i = buf.indexOf('\n\n')
    while (i >= 0) {
      const chunk = buf.slice(0, i)
      buf = buf.slice(i + 2)
      for (const line of chunk.split('\n')) {
        if (!line.startsWith('data:')) continue
        const data = line.slice(5).trim()
        if (!data) continue
        try {
          onEvent(JSON.parse(data) as E)
        } catch {
          // ignore malformed frames
        }
      }
      i = buf.indexOf('\n\n')
    }
  }
}

export async function download(path: string, fallbackName: string) {
  const res = await fetch(`/api${path}`, { credentials: 'same-origin' })
  if (!res.ok) await handle(res)
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}
